import { useState, useEffect, startTransition } from 'react';
import { Button, Col, Form, InputGroup, Nav, Row, Spinner } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { namespaces } from "../../i18n/i18n.constants";
import { Formik } from "formik";
import * as yup from "yup";
import { LoginRequest } from "../../app/models/api/auth/loginRequest";
import { useTakeTokenMutation } from "../../app/api/authApi";
import { ErrorResponse } from "../../app/models/api/errorResponse";
import { useAppDispatch } from '../../app/store';
import { setErrorMessage } from '../../pages/layoutes/authLayout/authSlice';
import { useAuth } from '../../hooks/useAuth';
import { Link, useLocation, useNavigate } from 'react-router';
import { getErrorMsgFromResponse } from '../../services/format/response';
import { routes } from '../../app/routes/routes';
import { useAuthLoading } from '../../pages/layoutes/authLayout/authLoadingContext';

const Login = () => {
    const { t } = useTranslation(namespaces.auth);
    const dispatch = useAppDispatch();
    const { setAuthLoading } = useAuthLoading();
    const [showPassword, setShowPassword] = useState(false);
    const toggleShowPassword = () => setShowPassword(!showPassword);
    const auth = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const emailFieldName = "email";
    const passwordFieldName = "password";

    const [loginHandler, { isLoading: isLoadingLoging, error: errorLogin }] = useTakeTokenMutation();

    useEffect(() => {
        setAuthLoading(isLoadingLoging);
    }, [isLoadingLoging, setAuthLoading]);

    useEffect(() => {
        if (auth.isAuthenticated) 
        {
            startTransition(() => {
                navigate(location.state?.from || '/');
            });
        }
    }, [auth.isAuthenticated, location.state?.from, navigate]);

    useEffect(() => {
        dispatch(setErrorMessage(getErrorMsgFromResponse(errorLogin as ErrorResponse)));
    }, [dispatch, errorLogin]);

    useEffect(() => {
        return () => {
            dispatch(setErrorMessage(""));
        };
      }, [dispatch]);

    return (
        <>
            <div className="login-body">
                <Formik
                        validationSchema={yup.object().shape({
                            email: yup
                                .string()
                                .email(t("email", { ns: namespaces.validation }))
                                .required(t("required.field", { ns: namespaces.validation })),
                            password: yup.string().required(t("required.field", { ns: namespaces.validation })),
                        })}
                        initialValues={{ email: "", password: "" } as LoginRequest}
                        onSubmit={async (values, { setSubmitting }) => {
                            try {
                                await loginHandler({
                                    email: values.email,
                                    password: values.password,
                                }).unwrap();
                            } catch {
                                // error is handled via errorLogin effect
                            } finally {
                                setSubmitting(false);
                            }
                        }}
                    >
                        {({ handleSubmit, handleChange, handleBlur, isSubmitting, values, touched, errors }) => (
                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="custom-group-controls mb-3" controlId={emailFieldName}>
                                    <Form.Label className="custom-form-label">{t("login.emailLbl")}</Form.Label>
                                    <Form.Control
                                        name={emailFieldName}
                                        className="custom-form-control"
                                        type="text"
                                        aria-label="email"
                                        autoFocus
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isValid={touched.email && !errors.email}
                                        isInvalid={touched.email && !!errors.email}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className="mb-3" controlId={passwordFieldName}>
                                    <Form.Label className="custom-form-label">{t("login.passwordLbl")}</Form.Label>
                                    <InputGroup className="custom-input-group">
                                        <Form.Control
                                            name={passwordFieldName}
                                            className="custom-form-control"
                                            type={showPassword ? "text" : "password"}
                                            aria-label="password"
                                            value={values.password}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            isValid={touched.password && !errors.password}
                                            isInvalid={touched.password && !!errors.password}
                                        />
                                        <InputGroup.Text className="password-hiding-block" onClick={toggleShowPassword}>
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </InputGroup.Text>
                                        <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                                    </InputGroup>
                                </Form.Group>
                                <Row className="my-0">
                                    <Col className="text-sm-start mb-2">
                                        <Nav.Link as={Link} className="orange-text" to={routes.forgotPassword.path}>
                                            {t("login.forgotPasswordLbl")}
                                        </Nav.Link>
                                    </Col>
                                </Row>
                                <Button variant="primary" type="submit" className="w-100 my-4 p-3" disabled={isSubmitting || isLoadingLoging}>
                                    {isLoadingLoging ? (
                                        <Spinner animation="border" size="sm" />
                                    ) : (
                                        t("login.signIn")
                                    )}
                                </Button>
                            </Form>
                        )}
                    </Formik>
                </div>
        </>
    );
};

export default Login;
